/**
 * MobileNav
 *
 * The customer-portal mobile chrome, below the lg breakpoint. Replaces the
 * phase-1 hamburger drawer entirely (ENGINE-BRIEF-MOBILE-NAV-v1.0 section 2; one
 * mobile model, not two). Built from WIREFRAME-MOBILE-NAV-v0.3 on role tokens.
 *
 * Four surfaces (brief section 4):
 *  - Top app bar: org context on the left, account avatar on the right. No brand
 *    (F2 ruled: the Home tab carries the dashboard-home affordance).
 *  - Bottom tab bar: Home, Bank, Create (centre, elevated), Invoices, More.
 *    Home/Bank/Invoices are NavLinks (aria-current native); Create/More open sheets.
 *  - Three Headless UI Dialog sheets: Create launchpad, More, Account. The library
 *    gives the focus trap, focus restore to the trigger, Escape and backdrop close.
 *
 * Tokens (brief section 7, ruled): the centre Create circle binds to bg-accent-cta
 * (dark in both themes, white at AA: 9.67 light, 5.15 dark) with text-on-accent.
 * accent-cta is the resolved section-7 fill; no accent-strong, no hex.
 *
 * iOS (brief section 8): the bar reserves env(safe-area-inset-bottom) under
 * viewport-fit=cover; the shell moves to dvh in Layout. Off iOS both resolve inert.
 *
 * F3 (org announcement): the org context is real text with a visually-hidden
 * "Organisation:" prefix, not an aria-label on a bare div, so it announces reliably
 * and survives the move to an org switcher later.
 */

import { useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from '@headlessui/react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme, ThemeIcons } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import { PRIMARY_NAV, orgInitials, Icons, CloseIcon, CheckIcon } from './TopNavModel';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

// ---------------------------------------------------------------------------
// Mobile glyphs (lifted from WIREFRAME-MOBILE-NAV-v0.3; currentColor, no colour set)
// ---------------------------------------------------------------------------

function mglyph(d: string, sw = 1.8) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} aria-hidden="true" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const MIcon = {
  home: () => mglyph('M3 11l9-8 9 8M5 10v10a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V10'),
  bank: () => mglyph('M3 21h18M4 21V10m4 11V10m8 11V10m4 11V10M3 10l9-5 9 5'),
  invoices: () => mglyph('M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'),
  plus: () => mglyph('M12 5v14M5 12h14', 2.2),
  grid: () => mglyph('M5 5h5v5H5zM14 5h5v5h-5zM5 14h5v5H5zM14 14h5v5h-5z'),
  quotes: () => mglyph('M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', 1.7),
  contacts: () => mglyph('M16 7a4 4 0 11-8 0 4 4 0 018 0zM4 21v-1a6 6 0 0112 0v1', 1.7),
  transactions: () => mglyph('M7 10l-3 3 3 3M4 13h13M17 14l3-3-3-3M20 11H7', 1.7),
  directors: () => mglyph('M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M4 7h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1zM4 12h16', 1.7),
  newTransaction: () => mglyph('M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4', 1.7),
  importStatement: () => mglyph('M12 16V4m0 0L8 8m4-4l4 4M4 20h16', 1.7),
  copyInvoice: () => mglyph('M8 8h10a2 2 0 012 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8a2 2 0 012-2zM16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h2', 1.7),
};

// ---------------------------------------------------------------------------
// Derived navigation data
// ---------------------------------------------------------------------------

// Bottom-bar destinations. Bank and Invoices are promoted out of the desktop
// primary; Home is the dashboard, new to the bottom bar.
const BOTTOM_LINKS = [
  { label: 'Home', to: '/', end: true, Icon: MIcon.home },
  { label: 'Bank', to: '/transactions/import', end: false, Icon: MIcon.bank },
  { label: 'Invoices', to: '/invoices', end: false, Icon: MIcon.invoices },
];
const BOTTOM_ROUTES = BOTTOM_LINKS.map((l) => l.to);

// "Go to" in the More sheet: the desktop primary items not on the bottom bar.
const GOTO_ICON: Record<string, () => JSX.Element> = {
  '/quotes': MIcon.quotes,
  '/contacts': MIcon.contacts,
  '/transactions': MIcon.transactions,
  '/directors': MIcon.directors,
};

// Create launchpad. Four items map to live routes and ship as links. Record payment
// is omitted (F-CHAIR-1): the capability already exists today, invoice-scoped, on the
// invoice detail page (invoicesApi.recordPayment), so "Record payment, coming" would be
// a false marker. It takes the Credit notes disposition, omit from the launchpad until a
// launchpad-initiated flow exists, not a badge. Copy last invoice has no capability
// anywhere in the app (invoicesApi has no duplicate), so it holds "coming" honestly.
type CreateItem =
  | { kind: 'link'; label: string; to: string; Icon: () => JSX.Element; span2?: boolean }
  | { kind: 'coming'; label: string; Icon: () => JSX.Element; span2?: boolean };

const CREATE_ITEMS: CreateItem[] = [
  { kind: 'link', label: 'New invoice', to: '/invoices/new', Icon: MIcon.invoices },
  { kind: 'link', label: 'New transaction', to: '/transactions/new', Icon: MIcon.newTransaction },
  { kind: 'link', label: 'Import statement', to: '/transactions/import', Icon: MIcon.importStatement },
  { kind: 'link', label: 'New quote', to: '/quotes/new', Icon: MIcon.quotes },
  { kind: 'coming', label: 'Copy last invoice', Icon: MIcon.copyInvoice, span2: true },
];

const themeOptions = [
  { value: 'light' as const, label: 'Light', Icon: ThemeIcons.Sun },
  { value: 'dark' as const, label: 'Dark', Icon: ThemeIcons.Moon },
  { value: 'system' as const, label: 'System', Icon: ThemeIcons.System },
];

// ---------------------------------------------------------------------------
// Shared row styles (sheets)
// ---------------------------------------------------------------------------

const rowBase =
  'flex items-center gap-3.5 w-full min-h-[50px] px-2.5 rounded-xl text-[15px] text-left transition-colors';
const rowIc = 'w-[22px] h-[22px] shrink-0';

function sheetLink(isActive: boolean) {
  return cn(
    rowBase,
    focusRing,
    isActive
      ? 'bg-accent-tint text-accent-strong font-semibold'
      : 'text-content hover:bg-surface-secondary'
  );
}

const sheetButton = cn(rowBase, focusRing, 'text-content hover:bg-surface-secondary');

const sheetSub = 'mx-2 mt-3 mb-1 font-mono text-[9.5px] uppercase tracking-[0.1em] text-content-tertiary';

// ---------------------------------------------------------------------------
// Bottom sheet wrapper (Headless UI Dialog: trap, restore, Escape, backdrop)
// ---------------------------------------------------------------------------

function Sheet({
  open,
  onClose,
  panelId,
  title,
  titleHidden,
  children,
}: {
  open: boolean;
  onClose: () => void;
  panelId: string;
  title: string;
  titleHidden?: boolean;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-[60] lg:hidden">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/50 dark:bg-black/70 transition-opacity duration-200 data-[closed]:opacity-0 motion-reduce:transition-none"
      />
      <div className="fixed inset-0 flex items-end justify-center">
        <DialogPanel
          id={panelId}
          transition
          className="w-full max-h-[86%] flex flex-col bg-surface border-t border-edge rounded-t-[22px] shadow-2xl pb-[calc(18px+env(safe-area-inset-bottom))] transition-transform duration-[240ms] ease-[cubic-bezier(.32,.72,0,1)] data-[closed]:translate-y-full motion-reduce:transition-none"
        >
          <div className="w-[38px] h-1 rounded-full bg-edge-strong mx-auto mt-[9px] mb-0.5 shrink-0" />
          <div className="flex items-center justify-between px-[18px] pt-1.5 pb-2.5 shrink-0">
            {titleHidden ? (
              <>
                <DialogTitle className="sr-only">{title}</DialogTitle>
                <span aria-hidden="true" />
              </>
            ) : (
              <DialogTitle className="text-base font-bold tracking-[-0.01em] text-content">{title}</DialogTitle>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={cn('w-11 h-11 -mr-1.5 rounded-[10px] bg-surface-secondary text-content-secondary flex items-center justify-center shrink-0', focusRing)}
            >
              <CloseIcon />
            </button>
          </div>
          <div className="overflow-auto px-3 pb-1.5">{children}</div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Create sheet
// ---------------------------------------------------------------------------

function CreateSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} panelId="mobile-sheet-create" title="Create">
      <div className="grid grid-cols-2 gap-2 px-1 pb-2">
        {CREATE_ITEMS.map((item) => {
          const span = item.span2 ? 'col-span-2' : '';
          if (item.kind === 'coming') {
            return (
              <div
                key={item.label}
                aria-label={`${item.label}, coming`}
                className={cn(
                  'flex gap-2.5 p-3.5 rounded-[13px] border border-dashed border-edge-strong text-content-tertiary',
                  item.span2 ? 'flex-row items-center' : 'flex-col items-start min-h-[88px]',
                  span
                )}
              >
                <span aria-hidden="true" className="w-[22px] h-[22px] shrink-0"><item.Icon /></span>
                <span className="text-[14px] font-medium" aria-hidden="true">{item.label}</span>
                <span aria-hidden="true" className="font-mono text-[8.5px] uppercase tracking-[0.06em] border border-dashed border-edge-strong rounded px-1.5 py-px ml-auto self-center">
                  coming
                </span>
              </div>
            );
          }
          return (
            <NavLink
              key={item.label}
              to={item.to}
              onClick={onClose}
              className={cn(
                'flex flex-col items-start gap-2.5 p-3.5 rounded-[13px] border border-edge bg-surface-secondary text-content min-h-[88px] transition-colors hover:bg-surface-hover',
                focusRing,
                span
              )}
            >
              <span className="w-[22px] h-[22px] text-accent"><item.Icon /></span>
              <span className="text-[14px] font-semibold">{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// More sheet
// ---------------------------------------------------------------------------

function MoreSheet({
  open,
  onClose,
  plan,
}: {
  open: boolean;
  onClose: () => void;
  plan?: string;
}) {
  const goto = PRIMARY_NAV.filter(
    (p) => !BOTTOM_ROUTES.includes(p.to) && (!p.requiresPlan || p.requiresPlan === plan)
  );

  return (
    <Sheet open={open} onClose={onClose} panelId="mobile-sheet-more" title="More">
      <p className={cn(sheetSub, 'first:mt-1')}>Go to</p>
      {goto.map((item) => {
        const Icon = GOTO_ICON[item.to];
        return (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onClose} className={({ isActive }) => sheetLink(isActive)}>
            {Icon && <span className={rowIc}><Icon /></span>}
            <span className="flex-1">{item.label}</span>
          </NavLink>
        );
      })}

      <p className={sheetSub}>Sales</p>
      <div className="flex items-center gap-3.5 min-h-[50px] px-2.5 rounded-xl text-[15px] text-content-tertiary" aria-label="Bills, coming">
        <span aria-hidden="true" className={rowIc}><Icons.bills /></span>
        <span className="flex-1" aria-hidden="true">Bills</span>
        <span aria-hidden="true" className="font-mono text-[8.5px] uppercase tracking-[0.06em] border border-dashed border-edge-strong rounded px-1.5 py-px">
          coming
        </span>
      </div>

      <p className={sheetSub}>Accounting</p>
      <NavLink to="/reports" onClick={onClose} className={({ isActive }) => sheetLink(isActive)}>
        <span className={rowIc}><Icons.reports /></span><span className="flex-1">Reports</span>
      </NavLink>
      <NavLink to="/accounts" onClick={onClose} className={({ isActive }) => sheetLink(isActive)}>
        <span className={rowIc}><Icons.accounts /></span>
        <span className="flex-1">Accounts</span>
        <span className="font-mono text-[11px] text-content-tertiary">chart of accounts</span>
      </NavLink>
      <NavLink to="/rules" onClick={onClose} className={({ isActive }) => sheetLink(isActive)}>
        <span className={rowIc}><Icons.rules /></span><span className="flex-1">Rules</span>
      </NavLink>

      <p className={sheetSub}>Setup</p>
      <NavLink to="/opening-balances" onClick={onClose} className={({ isActive }) => sheetLink(isActive)}>
        <span className={rowIc}><Icons.opening /></span><span className="flex-1">Opening balances</span>
      </NavLink>

      <p className={sheetSub}>Admin</p>
      <NavLink to="/logs" onClick={onClose} className={({ isActive }) => sheetLink(isActive)}>
        <span className={rowIc}><Icons.logs /></span><span className="flex-1">Logs</span>
      </NavLink>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Account sheet
// ---------------------------------------------------------------------------

function AccountSheet({
  open,
  onClose,
  onOpenBugReport,
}: {
  open: boolean;
  onClose: () => void;
  onOpenBugReport: () => void;
}) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const orgName = user?.organisation?.name ?? '';

  return (
    <Sheet open={open} onClose={onClose} panelId="mobile-sheet-account" title="Account" titleHidden>
      <div className="flex items-center gap-3 px-2.5 pt-1 pb-3.5 mb-1.5 border-b border-edge">
        <span aria-hidden="true" className="w-[38px] h-[38px] rounded-[10px] bg-accent-tint text-accent-strong flex items-center justify-center font-mono text-sm font-semibold shrink-0">
          {orgInitials(orgName)}
        </span>
        <div className="min-w-0">
          <div className="text-base font-bold text-content truncate">{orgName}</div>
          <div className="text-xs text-content-tertiary truncate">{user?.fullName} &middot; signed in</div>
        </div>
      </div>

      <NavLink to="/settings" onClick={onClose} className={({ isActive }) => sheetLink(isActive)}>
        <span className={rowIc}><Icons.settings /></span><span className="flex-1">Settings</span>
      </NavLink>

      <p className={sheetSub}>Theme</p>
      {themeOptions.map((opt) => {
        const current = theme === opt.value;
        const Icon = opt.Icon;
        return (
          <button key={opt.value} type="button" onClick={() => setTheme(opt.value)} className={cn(sheetButton, current && 'text-content font-medium')}>
            <span className={cn(rowIc, current ? 'text-accent' : 'text-content-tertiary')}><Icon /></span>
            <span className="flex-1">{opt.label}</span>
            {current && <span className="sr-only"> (current)</span>}
            {current && <span aria-hidden="true" className="text-accent"><CheckIcon /></span>}
          </button>
        );
      })}

      {user?.isAdmin && (
        <NavLink to="/admin" onClick={onClose} className={({ isActive }) => sheetLink(isActive)}>
          <span className={rowIc}><Icons.admin /></span><span className="flex-1">Admin panel</span>
        </NavLink>
      )}

      <button type="button" onClick={() => { onClose(); onOpenBugReport(); }} className={sheetButton}>
        <span className={rowIc}><Icons.bug /></span><span className="flex-1">Report a bug</span>
      </button>

      <button type="button" onClick={() => { onClose(); logout(); }} className={sheetButton}>
        <span className={rowIc}><Icons.signout /></span><span className="flex-1">Sign out</span>
      </button>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// MobileNav
// ---------------------------------------------------------------------------

type SheetKey = 'create' | 'more' | 'account';

export default function MobileNav({ onOpenBugReport }: { onOpenBugReport: () => void }) {
  const { user } = useAuth();
  const [sheet, setSheet] = useState<SheetKey | null>(null);

  const orgName = user?.organisation?.name ?? '';
  const plan = user?.organisation?.plan;
  const firstName = (user?.fullName ?? '').trim().split(/\s+/)[0] || 'account';
  const avatarInitial = (user?.fullName ?? '?').trim().charAt(0).toUpperCase() || '?';

  const close = () => setSheet(null);

  return (
    <>
      {/* Top app bar: context only, no brand (F2). Reserves the top safe-area inset
          for standalone display; 0 in plain Safari. */}
      <header className="lg:hidden shrink-0 flex items-center gap-3 h-[54px] px-3.5 bg-surface border-b border-edge pt-[env(safe-area-inset-top)]">
        {/* F3: real text with a visually-hidden prefix, not aria-label on a bare div. */}
        <div className="flex items-center gap-2.5 min-w-0">
          <span aria-hidden="true" className="w-7 h-7 rounded-[7px] bg-accent-tint text-accent-strong flex items-center justify-center font-mono text-[11px] font-semibold shrink-0">
            {orgInitials(orgName)}
          </span>
          <span className="text-[15px] font-semibold tracking-[-0.01em] text-content truncate">
            <span className="sr-only">Organisation: </span>{orgName}
          </span>
        </div>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => setSheet('account')}
          aria-haspopup="dialog"
          aria-expanded={sheet === 'account'}
          aria-controls="mobile-sheet-account"
          aria-label={`Account, ${firstName}`}
          className={cn('w-11 h-11 rounded-full border border-edge-strong bg-surface-secondary text-content flex items-center justify-center font-semibold text-sm shrink-0', focusRing)}
        >
          {avatarInitial}
        </button>
      </header>

      {/* Bottom tab bar: thumb zone. Reserves env(safe-area-inset-bottom); the tap
          targets sit above the reserved band (C-IOS-2/3). */}
      <nav
        aria-label="Primary"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 flex items-start justify-around bg-surface border-t border-edge px-1.5 pt-2 pb-[env(safe-area-inset-bottom)]"
      >
        <BottomTab {...BOTTOM_LINKS[0]} />
        <BottomTab {...BOTTOM_LINKS[1]} />

        <div className="flex-1 flex flex-col items-center gap-[3px]">
          <button
            type="button"
            onClick={() => setSheet('create')}
            aria-haspopup="dialog"
            aria-expanded={sheet === 'create'}
            aria-controls="mobile-sheet-create"
            aria-label="Create"
            className={cn('w-[54px] h-[54px] -mt-4 rounded-full bg-accent-cta text-on-accent border-[3px] border-surface flex items-center justify-center shadow-lg', focusRing)}
          >
            <span className="w-[25px] h-[25px]"><MIcon.plus /></span>
          </button>
          <span className="text-[10.5px] font-medium text-content-tertiary mt-0.5">Create</span>
        </div>

        <BottomTab {...BOTTOM_LINKS[2]} />

        <button
          type="button"
          onClick={() => setSheet('more')}
          aria-haspopup="dialog"
          aria-expanded={sheet === 'more'}
          aria-controls="mobile-sheet-more"
          className={cn('relative flex-1 flex flex-col items-center gap-[3px] min-h-[54px] pt-1.5 text-content-tertiary', focusRing)}
        >
          <span className="w-[23px] h-[23px]"><MIcon.grid /></span>
          <span className="text-[10.5px] font-medium">More</span>
        </button>
      </nav>

      <CreateSheet open={sheet === 'create'} onClose={close} />
      <MoreSheet open={sheet === 'more'} onClose={close} plan={plan} />
      <AccountSheet open={sheet === 'account'} onClose={close} onOpenBugReport={onOpenBugReport} />
    </>
  );
}

function BottomTab({ label, to, end, Icon }: { label: string; to: string; end: boolean; Icon: () => JSX.Element }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'relative flex-1 flex flex-col items-center gap-[3px] min-h-[54px] pt-1.5 transition-colors',
          focusRing,
          isActive ? 'text-accent' : 'text-content-tertiary'
        )
      }
    >
      {({ isActive }) => (
        <>
          <span className={cn('absolute top-0 w-[22px] h-[2.5px] rounded-full', isActive ? 'bg-accent' : 'bg-transparent')} />
          <span className="w-[23px] h-[23px]"><Icon /></span>
          <span className={cn('text-[10.5px]', isActive ? 'font-semibold' : 'font-medium')}>{label}</span>
        </>
      )}
    </NavLink>
  );
}
